import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { CobaltButtonModule, CobaltInputModule, IconComponent } from '@msi/cobalt-prime';
import { Subscription, timer } from 'rxjs';


@Component({
  selector: 'app-search-input',
  templateUrl: './search-input.component.html',
  styleUrls: ['./search-input.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslocoModule,
    CobaltButtonModule,
    CobaltInputModule,
    IconComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchInputComponent implements OnInit {
  @Input() placeholder: string = '';
  @Input() ariaLabel: string = '';
  @Input() disabled: boolean = false;
  @Input() initialValue: string = '';
  @Input() clearOnEmpty: boolean = true;
  @Input() isSearchActive: boolean = false;
  @Input() isLoading: boolean = false;

  @Output() searchExecuted = new EventEmitter<string>();
  @Output() searchCleared = new EventEmitter<void>();
  @Output() searchCancelled = new EventEmitter<void>();  

  public searchTerm: string = '';
  private lastSearchTerm: string = '';

  private readonly searchDelayMs: number = 300;
  private readonly minCharLength: number = 3;
  private searchTimerSubscription?: Subscription;

  ngOnInit(): void {
    if (this.initialValue) {
      this.searchTerm = this.initialValue;
    }
  }

  public executeSearch(): void {
    if (this.disabled) {
      return;
    }
    this.lastSearchTerm = this.searchTerm;
    this.searchExecuted.emit(this.searchTerm);
  }

  public onSearchTermChanged(newValue: string): void {
    // Cancel previous search timer
    if (this.searchTimerSubscription) {
      this.searchTimerSubscription.unsubscribe();
    }

    // Handle empty/whitespace - cancel search and restore cached state
    if (this.clearOnEmpty 
      && (!newValue || newValue.trim() === '') 
      && this.lastSearchTerm.length >= this.minCharLength) {
      this.searchTerm = '';
      this.searchCancelled.emit();
      return;
    }

    // Only auto-search if minimum character length is met
    if (newValue.length >= this.minCharLength) {
      this.searchTimerSubscription = timer(this.searchDelayMs).subscribe(() => {
        this.executeSearch();
      });
    }
  }

  public onEnterKeyPressed(): void {
    this.executeSearch();
  }

  public onEscapeKeyPressed(): void {
    if (this.isSearchActive && this.searchTerm) {
      this.cancelSearch();
    }
  }

  public cancelSearch(): void {
    if (this.disabled) {
      return;
    }
    this.searchTerm = '';
    this.lastSearchTerm = '';
    this.searchCancelled.emit();
  }
}
